import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Accordion, Row, Col, Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import TopNav from "../../TopNav/TopNav";
import JobNav from "../JobNav";
import JobGeneralOverview from "../JobOverviewBar";
import CheckoutNotification from "../checkoutNotification";
import HCRequirements from "../Mechanical/HCRequirements";
import StationEnclosure from "../Mechanical/StationEnclosure";
import LevelControls from "../Mechanical/LevelControls";
import Filtration from "../Mechanical/Filtration/Filtration";
import ShipLoose from "../Mechanical/ShipLoose";
import ShipEarlyForm from "../../ShipEarlyForm";
import useHandleBlur from "../../../functions/useHandleBlur";
import SaveBanner from "../Mechanical/SaveBanner";
import { normalizeToggleValue } from "../../../utils/toggleValues";
import { selectUserDetails, selectUserRoles } from "../../../redux/slices/userSlice";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import {
  selectGeneralStation,
  selectStationEnclosure,
  selectDropPipeComments,
  selectLevelControls,
  selectPumps,
  selectWaterTreatment,
  selectTelemetry,
  selectSequenceOfOperations,
  selectUnregulatedPower,
  selectRegulatedPower,
  selectControlLogicOnly,
  selectFertigation,
  selectShipLoose,
} from "../../../redux/selectors";
import PumpTable from "../Mechanical/Pumps/PumpTable"; // Assuming PumpTable is in this path
import Options from "./Options"; // Assuming Options is in this path
import UnRegulatedPower from "./UnRegulatedPower"; // Assuming UnRegulatedPower is in this path
import RegulatedPower from "./RegulatedPower"; // Assuming RegulatedPower is in this path
import Telemetry from "./Telemetry"; // Assuming Telemetry is in this path
import SequenceOfOperations from "./SequenceOfOperations"; // Assuming SequenceOfOperations is in this path
import useJobHandler, { useJobSync } from "../../../functions/JobHandler";
import GeneralElectrical from "./GeneralElectrical"; // Import GeneralElectrical component
import ControlLogicOnly from "./ControlLogicOnly";
import { selectApiStatus } from "../../../redux/slices/apiStatusSlice";
import { selectFullJobFetchLoading } from "../../../redux/slices/fullJobFetchSlice";
import Fertigation from "../Mechanical/Fertigation"; // Import Fertigation component
import ZoneButtons from "../ZoneButtons";
import InfoBubbleModal from "../../common/InfoBubbleModal";
import { getZoneItemCount, getShipEarlyItemCount } from "../../../utils/itemCountUtils";

// Add selector for shipEarlyParent
const EMPTY_ARRAY = [];
const selectShipEarlyParent = (state) => state.jobDetails.shipEarlyParent || EMPTY_ARRAY;
const JOB_TRACE_KEY = "debugElecLoadTrace";

const isTraceEnabled = () => {
  if (process.env.NODE_ENV !== "development") {
    return false;
  }

  try {
    return window.localStorage.getItem(JOB_TRACE_KEY) === "1";
  } catch {
    return false;
  }
};

const traceLog = (message, payload) => {
  if (!isTraceEnabled()) {
    return;
  }

  if (payload !== undefined) {
    console.log(`[JobTrace:Elec] ${message}`, payload);
    return;
  }

  console.log(`[JobTrace:Elec] ${message}`);
};

function JobElecSpecs() {
  const currentKey = "elecInfo";
  const userDetails = useSelector(selectUserDetails);
  const currentUser = userDetails?.name;
  const userRoles = useSelector(selectUserRoles); // User details
  const [roles, setRoles] = useState(mapUserRoles(userRoles));
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const apiStatus = useSelector(selectApiStatus); // Get API status from Redux
  const fullJobLoading = useSelector(selectFullJobFetchLoading); // Get fullJobFetch loading state

  const jobId = useSelector((state) => state.jobId.jobId);
  const apiUrls = useSelector(selectApiUrls);

  // Check if APIs are still loading
  const [apisLoading, setApisLoading] = useState(true);
  
  // Arrays of API sections that need to be loaded
  const requiredApis = useMemo(() => [
    'generalStation',
    'hcRequirements', 
    'stationEnclosure',
    'dropPipeComments',
    'levelControls',
    'pumpManifoldPiping',
    'fertigation',
    'pumps',
    'waterTreatment',
    'unregulatedPower',
    'regulatedPower',
    'sequenceOfOperations',
    'controlLogicOnly',
    'electricalOptions',
    'telemetry',
    'telemetryAccessories',
    'shipLoose',
    'shipEarlyParent'
  ], []);

  // Check API loading status
  useEffect(() => {
    // Function to check if required APIs are loaded
    const checkApisStatus = () => {
      const statusKeys = Object.keys(apiStatus);

      // If fullJobFetch is actively loading, show loading state
      if (fullJobLoading) {
        // If no section statuses exist yet, still loading initial payload
        if (statusKeys.length === 0) {
          return true;
        }

        // If any required section has already settled, don't let global loading pin the page
        const hasSettledSection = requiredApis.some(
          (api) => apiStatus[api] === "success" || apiStatus[api] === "failed"
        );

        if (!hasSettledSection) {
          return true;
        }
      }

      // If apiStatus is empty and not loading, APIs haven't started yet
      if (statusKeys.length === 0) {
        return false;
      }

      // Check if any required API is still pending
      const stillLoading = requiredApis.some(api => 
        apiStatus[api] === 'pending'
      );

      traceLog("checkApisStatus evaluation", {
        jobId,
        fullJobLoading,
        statusKeysCount: statusKeys.length,
        pendingRequiredApis: requiredApis.filter((api) => apiStatus[api] === "pending"),
        settledRequiredApis: requiredApis.filter((api) => apiStatus[api] === "success" || apiStatus[api] === "failed"),
        stillLoading,
      });
      
      return stillLoading;
    };

    setApisLoading(checkApisStatus());
  }, [apiStatus, requiredApis, fullJobLoading, jobId]);

  const generalStationDetails = useSelector(selectGeneralStation);
  const stationEnclosureDetails = useSelector(selectStationEnclosure);
  const dropPipeCommentDetails = useSelector(selectDropPipeComments);
  const levelControlsDetails = useSelector(selectLevelControls);
  const fertigationDetails = useSelector(selectFertigation)
  const pumpDetails = useSelector(selectPumps);
  const waterTreatment = useSelector(selectWaterTreatment);
  const unregulatedPowerDetails = useSelector(selectUnregulatedPower);
  const regulatedPowerDetails = useSelector(selectRegulatedPower);
  const sequenceOfOperationsDetails = useSelector(selectSequenceOfOperations);
  const controlLogicOnlyDetails = useSelector(selectControlLogicOnly);
  const telemetryDetails = useSelector(selectTelemetry);
  const shipLooseDetails = useSelector(selectShipLoose);
  const shipEarlyParentDetails = useSelector(selectShipEarlyParent);

  const activeTabNumber = useSelector(
    (state) => state.activeMechanicalTab.selectedZone || 1 // Default to 1 if undefined
  );

  useEffect(() => {
    traceLog("render gate snapshot", {
      jobId,
      apisLoading,
      fullJobLoading,
      apiStatusKeys: Object.keys(apiStatus),
      generalStationCount: generalStationDetails?.length || 0,
      stationEnclosureCount: stationEnclosureDetails?.length || 0,
      levelControlsCount: levelControlsDetails?.length || 0,
      telemetryCount: telemetryDetails?.length || 0,
      shipEarlyParentCount: shipEarlyParentDetails?.length || 0,
      activeTabNumber,
    });
  }, [jobId, apisLoading, fullJobLoading, apiStatus, generalStationDetails, stationEnclosureDetails, levelControlsDetails, telemetryDetails, shipEarlyParentDetails, activeTabNumber]);

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  useJobHandler();
  
  // Sync job data when this page loads
  const { syncJob } = useJobSync();
  const enableMountSync = process.env.REACT_APP_ENABLE_MOUNT_SYNC === "true";
  
  useEffect(() => {
    if (jobId && enableMountSync) {
      console.log("JobElecInfo: Syncing job data for jobId:", jobId);
      traceLog("syncJob start", { jobId });
      syncJob({ reuseRecent: true })
        .then((result) => {
          if (result && result.fieldsUpdated > 0) {
            console.log(`JobElecInfo: Updated ${result.fieldsUpdated} fields across ${result.sectionsUpdated} sections`);
          } else {
            console.log("JobElecInfo: Job data is already up to date");
          }
          traceLog("syncJob success", { jobId, result });
        })
        .catch((error) => {
          console.error("JobElecInfo: Failed to sync job data:", error);
          traceLog("syncJob failed", { jobId, error: error?.message || error });
        });
    }
  }, [jobId, syncJob, enableMountSync]);

  // useEffect(() => {
  //   dispatch(clearApiStatus());
  // }, [dispatch]);

  // useEffect(() => {
  //   if (jobId) {
  //     dispatch((dispatch, getState) => fetchMissingJobDetails(dispatch, getState, jobId));
  //   }
  // }, [jobId, dispatch]);

  const tbdForms = useSelector((state) => state.tbd?.forms || {});

  const [isHcTrackerType, setIsHcTrackerType] = useState(false);
  const [accordionToggles, setAccordionToggles] = useState({});
  const [sectionDetails, setSectionDetails] = useState({});

  // Info bubble modal states
  const [showInfoBubbleModal, setShowInfoBubbleModal] = useState(false);
  const [infoBubbleSection, setInfoBubbleSection] = useState({ sectionName: '', value: '', title: '' });

  const {
    handleBlur,
    showNotification,
    saveStatus,
  } = useHandleBlur();

  const [loadingSections] = useState({
    generalStationDetails: true,
  });

  const location = useLocation();
  const { state } = location;
  const tbdItem = state?.tbdItem;

  const sections = useMemo(
    () => [
      {
        name: "Options",
        component: Options,
        hasToggle: false,
        apiUrl: apiUrls.electricalOptions,
        tableKey: "electricalOptions",
      },
      {
        name: "Station Enclosure",
        component: StationEnclosure,
        hasToggle: true,
        apiUrl: apiUrls.stationEnclosure,
        field: "HasEnclosure",
        tableKey: "stationEnclosure",
      },
      {
        name: "Level Controls",
        component: LevelControls,
        hasToggle: true,
        apiUrl: apiUrls.levelControls,
        field: "HasLevelControls",
        tableKey: "levelControls",
      },
      {
        name: "Fertigation",
        component: Fertigation,
        hasToggle: true,
        apiUrl: apiUrls.fertigation,
        field: "HasFertigation",
        tableKey: "fertigation",
      },
      {
        name: "Water Treatment",
        component: Filtration,
        hasToggle: true,
        apiUrl: apiUrls.waterTreatment,
        field: "HasFilters",
        tableKey: "waterTreatment",
      },
      {
        name: "Unregulated Power",
        component: UnRegulatedPower,
        hasToggle: true,
        apiUrl: apiUrls.unregulatedPower,
        field: "HasUnregulated",
        tableKey: "unregulatedPower",
      },
      {
        name: "Regulated Power",
        component: RegulatedPower,
        hasToggle: true,
        apiUrl: apiUrls.regulatedPower,
        field: "HasRegulated",
        tableKey: "regulatedPower",
      },
      {
        name: "Telemetry",
        component: Telemetry,
        hasToggle: true,
        apiUrl: apiUrls.telemetry,
        field: "HasTelemetry",
        tableKey: "telemetry",
      },      
      {
        name: "Sequence of Operations",
        component: SequenceOfOperations,
        hasToggle: false,
        apiUrl: apiUrls.sequenceOfOperations,
        tableKey: "sequenceOfOperations",
      },
      {
        name: "Control Logic",
        component: ControlLogicOnly,
        hasToggle: true,
        apiUrl: apiUrls.controlLogicOnly,
        field: "HasControlLogic",
        tableKey: "controlLogicOnly",
      },
      {
        name: "Ship Loose",
        component: ShipLoose,
        hasToggle: true,
        apiUrl: apiUrls.shipLoose,
        field: "HasShipLoose",
        tableKey: "shipLoose",
      },
      {
        name: "Ship Early/ Direct Form",
        component: ShipEarlyForm,
        hasToggle: false, // No toggle for Ship Early/ Direct Form
        apiUrl: apiUrls.shipEarlyParent,
        field: "HasShipEarly",
        isSpecial: true, // Mark as special to handle differently
        tableKey: "shipEarlyParent",
      },
    ],
    [apiUrls]
  );

  const expandAccordion = useCallback((tbdItem) => {
    // Implement the logic to expand the accordion
    const sectionName = tbdItem.Table;
    const sectionIndex = sections.findIndex(section => section.name.toLowerCase() === sectionName.toLowerCase());
    if (sectionIndex !== -1) {
      setAccordionToggles(prevToggles => ({
        ...prevToggles,
        [sections[sectionIndex].name]: "yes"
      }));
    }
  }, [sections]);

  useEffect(() => {
    if (tbdItem) {
      // Logic to expand the corresponding accordion based on tbdItem
      expandAccordion(tbdItem);
    }
  }, [tbdItem, jobId, expandAccordion]);

  useEffect(() => {
    if (generalStationDetails.length > 0) {
      const matchingSpecs = generalStationDetails.find(
        (spec) => spec.Zone === activeTabNumber
      );
      setIsHcTrackerType(matchingSpecs?.TrackerType === "HC" || false);
    }
  }, [generalStationDetails, activeTabNumber]);

  useEffect(() => {
    const initialToggles = sections.reduce((acc, section) => {
      acc[section.name] = section.name === "Sequence of Operations" || section.name === "Options" ? "yes" : "no";
      return acc;
    }, {});

    const initialDetails = {};

    if (stationEnclosureDetails.length > 0) {
      const matchingStationEnclosure = stationEnclosureDetails.find(
        (enclosure) => enclosure.Zone === activeTabNumber
      );
      if (matchingStationEnclosure) {
        initialToggles["Station Enclosure"] =
          normalizeToggleValue(matchingStationEnclosure.HasEnclosure) ? "yes" : "no";
        initialDetails["Station Enclosure"] = matchingStationEnclosure.ID;
      }
    }
    if (dropPipeCommentDetails.length > 0) {
      const matchingDropPipeComments = dropPipeCommentDetails.find(
        (droppipe) => droppipe.Zone === activeTabNumber
      );
      if (matchingDropPipeComments) {
        initialToggles["Drop Pipes"] =
          normalizeToggleValue(matchingDropPipeComments.HasDropPipes) ? "yes" : "no";
        initialDetails["Drop Pipes"] = matchingDropPipeComments.ID;
      }
    }
    if (levelControlsDetails.length > 0) {
      const matchingLevelControls = levelControlsDetails.find(
        (levelcontrol) => levelcontrol.Zone === activeTabNumber
      );
      if (matchingLevelControls) {
        initialToggles["Level Controls"] =
          normalizeToggleValue(matchingLevelControls.HasLevelControls) ? "yes" : "no";
        initialDetails["Level Controls"] = matchingLevelControls.ID;
      }
    }
    if (fertigationDetails.length > 0) {
      const matchingFertigation = fertigationDetails.find(
        (fertigation) => fertigation.Zone === activeTabNumber
      );
      if (matchingFertigation) {
        initialToggles["Fertigation"] =
          normalizeToggleValue(matchingFertigation.HasFertigation) ? "yes" : "no";
        initialDetails["Fertigation"] = matchingFertigation.ID;
      }
    }
    if (pumpDetails.length > 0) {
      const matchingPumps = pumpDetails.find(
        (pump) => pump.Zone === activeTabNumber
      );
      if (matchingPumps) {
        initialToggles["Pumps"] = normalizeToggleValue(matchingPumps.HasMain) ? "yes" : "no";
        initialDetails["Pumps"] = matchingPumps.ID;
      }
    }
    if (waterTreatment.length > 0) {
      const matchingFilters = waterTreatment.find(
        (filter) => filter.Zone === activeTabNumber
      );
      if (matchingFilters) {
     
        initialToggles["Water Treatment"] =
        normalizeToggleValue(matchingFilters.HasFilters) ? "yes" : "no";
        initialDetails["Water Treatment"] = matchingFilters.ID; // Assuming ProjectID is the ID we need
      }
    }
    if (unregulatedPowerDetails.length > 0) {
      const matchingUnRegulatedPower = unregulatedPowerDetails.find(
      (unregulatedPower) => unregulatedPower.Zone === activeTabNumber
      );
      if (matchingUnRegulatedPower) {
      initialToggles["Unregulated Power"] =
        normalizeToggleValue(matchingUnRegulatedPower.HasUnregulated) ? "yes" : "no";
      initialDetails["Unregulated Power"] = matchingUnRegulatedPower.ID;
      }
    }
    if (regulatedPowerDetails.length > 0) {
      const matchingRegulatedPower = regulatedPowerDetails.find(
      (regulatedPower) => regulatedPower.Zone === activeTabNumber
      );
      if (matchingRegulatedPower) {
      initialToggles["Regulated Power"] =
        normalizeToggleValue(matchingRegulatedPower.HasRegulated) ? "yes" : "no";
      initialDetails["Regulated Power"] = matchingRegulatedPower.ID;
      }
    }
    if (telemetryDetails.length > 0) {
      const matchingTelemetry = telemetryDetails.find(
      (telemetry) => telemetry.Zone === activeTabNumber
      );
      if (matchingTelemetry) {
      initialToggles["Telemetry"] =
        normalizeToggleValue(matchingTelemetry.HasTelemetry) ? "yes" : "no";
      initialDetails["Telemetry"] = matchingTelemetry.ID;
      }
    }
    if (controlLogicOnlyDetails.length > 0) {
      const matchingControlLogicOnly = controlLogicOnlyDetails.find(
      (controlLogicOnly) => controlLogicOnly.Zone === activeTabNumber
      );
      if (matchingControlLogicOnly) {
      initialToggles["Control Logic"] =
        normalizeToggleValue(matchingControlLogicOnly.HasControlLogic) ? "yes" : "no";
      initialDetails["Control Logic"] = matchingControlLogicOnly.ID;
      }
    }
    if (shipLooseDetails.length > 0) {
      const matchingShipLoose = shipLooseDetails.find(
      (shipLoose) => shipLoose.Zone === activeTabNumber
      );
      if (matchingShipLoose) {
      initialToggles["Ship Loose"] =
        normalizeToggleValue(matchingShipLoose.HasShipLoose) ? "yes" : "no";
      initialDetails["Ship Loose"] = matchingShipLoose.ID;
      }
    }

    // Check if Ship Early/ Direct Form should be enabled based on shipEarlyParent records
    if (shipEarlyParentDetails.length > 0) {
      // Check if any record matches the current jobId
      const hasShipEarlyRecord = shipEarlyParentDetails.some(
        (record) => String(record.ProjectID) === String(jobId)
      );
      initialToggles["Ship Early/ Direct Form"] = hasShipEarlyRecord ? "yes" : "no";
    }

    setAccordionToggles(initialToggles);
    setSectionDetails(initialDetails);
  }, [stationEnclosureDetails, activeTabNumber, sections, dropPipeCommentDetails, levelControlsDetails, pumpDetails, waterTreatment, unregulatedPowerDetails, regulatedPowerDetails, sequenceOfOperationsDetails, telemetryDetails, controlLogicOnlyDetails, fertigationDetails, shipLooseDetails, shipEarlyParentDetails, jobId]);

  const hasTbdForSection = (section) => {
    const sectionFormMap = {
      "Options": ["electricalOptions"],
      "Station Enclosure": ["stationEnclosure"],
      "Level Controls": ["levelControls", "fillSources"],
      "Fertigation": ["fertigation"],
      "Water Treatment": ["waterTreatment", "inletScreen", "inletStrainer", "dischargeFilter", "dischargeStrainer", "roSystem", "uvSystem"],
      "Unregulated Power": ["unregulatedPower", "unregulatedBreakers"],
      "Regulated Power": ["regulatedPower", "regulatedBreakers"],
      "Telemetry": ["telemetry", "telemetryAccessories"],
      "Sequence of Operations": ["sequenceOfOperations"],
      "Control Logic": ["controlLogicOnly"],
      "Ship Loose": ["shipLoose"],
      "Ship Early/ Direct Form": ["shipEarlyParent", "shipEarlyItems"],
      "HC Requirements": ["hcRequirements"],
    };

    const formKeys = sectionFormMap[section.name] || [];
    return formKeys.some((formKey) => Boolean(tbdForms[formKey]?.hasTbd));
  };

  return (
    <div>
      <TopNav />
      <JobGeneralOverview />
      <JobNav keyValue={currentKey} />
      <SaveBanner showNotification={showNotification} saveStatus={saveStatus} />
      <div className="mb-3 m-2">
        <ZoneButtons activeTabNumber={activeTabNumber} />
        <CheckoutNotification currentUser={currentUser} />
        
        {apisLoading && (
          <div className="text-center py-3">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading data...</span>
            </div>
            <p className="mt-2">Loading section data...</p>
          </div>
        )}
        
        <GeneralElectrical zone={activeTabNumber} /> {/* GeneralElectrical component */}
        <div className="electrical-pump-table">
          <PumpTable isJobMechInfo={false} />
        </div>
      </div>

      <Accordion alwaysOpen>
        {[
          ...(isHcTrackerType
            ? [{ component: HCRequirements, name: "HC Requirements" }]
            : []),
          ...sections,
        ].map((section, index) => {
          if (!section || !section.name) {
            return null;
          }

          const SectionComponent = section.component;
          const isDisabled = section.isSpecial 
            ? false // Ship Early/ Direct Form is never disabled, but controlled by its own logic
            : (accordionToggles[section.name] === "no");
          const sectionId = sectionDetails[section.name];
          const tableKey = section.tableKey || 
            (section.name.charAt(0).toLowerCase() +
            section.name
              .slice(1)
              .replace(/[^a-zA-Z0-9]/g, "")
              .replace(/\s+/g, ""));

          return (
            <Accordion.Item
              eventKey={String(index)}
              key={section.name}
              className={isDisabled ? "disabled-accordion" : ""}
            >
              <Accordion.Header className={isDisabled ? "disabled-header" : ""}>
                <Row className="w-100 align-items-center">
                  {section.hasToggle && (
                    <Col xs="auto" className="d-flex align-items-center">
                      <Form.Check
                        className="large-toggle"
                        type="switch"
                        id={`${section.name}-toggle`}
                        label=""
                        checked={accordionToggles[section.name] === "yes"}
                        disabled={readOnly || (!roles.Admin && !roles.Apps) || apisLoading}
                        
                        onChange={(e) => {
                          const newValue = e.target.checked ? "yes" : "no";
                          // Update local state immediately
                          setAccordionToggles((prevToggles) => ({
                            ...prevToggles,
                            [section.name]: newValue,
                          }));
                          
                          // Only make API call if we have a section ID and APIs are done loading
                          if (sectionId && !apisLoading) {
                            console.log("Toggle clicked in JobElecInfo:", {
                              sectionId: sectionId,
                              section: section.name,
                              field: section.field,
                              tableKey: tableKey,
                              newValue: newValue,
                              apiUrl: section.apiUrl
                            });
                            
                            handleBlur(
                              {
                                target: {
                                  name: section.name,
                                  value: newValue === "yes", // Pass as boolean value
                                  type: "switch"
                                },
                              },
                              section.field, // Use the field property for consistency
                              [],
                              {
                                ID: sectionId,
                                url: section.apiUrl,
                                field: section.field,
                                tableKey: tableKey,
                                zone: activeTabNumber
                              }
                            );
                          }
                        }}
                        style={{ pointerEvents: "auto" }}
                      />
                    </Col>
                  )}
                  {!section.hasToggle && <Col xs="auto" />}
                  <Col>
                    <h3>
                      {hasTbdForSection(section) && (
                        <i
                          className="bi bi-exclamation-diamond-fill"
                          style={{ color: "red" }}
                        />
                      )}{" "}
                      {section.name}
                      {section.name === "Ship Loose" && accordionToggles[section.name] === "yes" && getZoneItemCount(shipLooseDetails, jobId)}
                      {section.name === "Control Logic" && accordionToggles[section.name] === "yes" && getZoneItemCount(controlLogicOnlyDetails, jobId)}
                      {section.name === "Ship Early/ Direct Form" && getShipEarlyItemCount(shipEarlyParentDetails, jobId)}
                    </h3>
                  </Col>
                </Row>
              </Accordion.Header>
              {(!isDisabled || section.isSpecial) && (
                <Accordion.Body>
                  {/* Info icon for Ship Loose, Ship Early/ Direct Form, and Control Logic */}
                  {(section.name === "Ship Loose" || section.name === "Ship Early/ Direct Form" || section.name === "Control Logic") && (
                    <div className="d-flex justify-content-start mb-3">
                      <i 
                        className="bi bi-info-circle text-primary" 
                        style={{ cursor: 'pointer', fontSize: '1rem' }}
                        onClick={() => {
                          setInfoBubbleSection({
                            sectionName: section.name.replace(/\s+/g, ''),
                            value: 'Page',
                            title: `${section.name} Information`
                          });
                          setShowInfoBubbleModal(true);
                        }}
                        title="Click for information"
                      ></i>
                    </div>
                  )}
                  
                  {!section.isSpecial && (
                    <h5 className="text-center text-muted mb-3">
                      {activeTabNumber === 1 ? "Zone 1" : "Zone 2"}
                    </h5>
                  )}
                  {loadingSections[section.name] ? (
                    <div className="d-flex justify-content-center align-items-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    SectionComponent && <SectionComponent />
                  )}
                </Accordion.Body>
              )}
            </Accordion.Item>
          );
        })}
      </Accordion>

      {/* Info Bubble Modal */}
      <InfoBubbleModal
        show={showInfoBubbleModal}
        onHide={() => setShowInfoBubbleModal(false)}
        sectionName={infoBubbleSection.sectionName}
        value={infoBubbleSection.value}
        title={infoBubbleSection.title}
        displayAsOrderedList={false}
      />
    </div>
  );
}

export default JobElecSpecs;
